import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

function slugify(value) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";

function ensureRecaptchaScript(src) {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (existingScript) {
      if (existingScript.getAttribute("src") !== src) {
        existingScript.remove();
        delete window.grecaptcha;
      } else {
        if (window.grecaptcha) {
          resolve();
          return;
        }
        existingScript.addEventListener("load", resolve, { once: true });
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }
    }

    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

async function executeRecaptchaV3(siteKey) {
  const scriptSrc = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
  await ensureRecaptchaScript(scriptSrc);

  return new Promise((resolve, reject) => {
    if (typeof window.grecaptcha?.ready !== "function") {
      reject(new Error("reCAPTCHA is not ready."));
      return;
    }

    window.grecaptcha.ready(() => {
      if (typeof window.grecaptcha?.execute !== "function") {
        reject(new Error("reCAPTCHA execute is not available."));
        return;
      }
      window.grecaptcha.execute(siteKey, { action: "signup" }).then(resolve).catch(reject);
    });
  });
}

function RecaptchaBox({ siteKey, mode = "checkbox", onTokenChange }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;
    let cancelled = false;
    let timer = null;
    const normalizedMode = mode === "v3" ? "v3" : "checkbox";
    const scriptSrc =
      normalizedMode === "v3"
        ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
        : "https://www.google.com/recaptcha/api.js?render=explicit";

    const renderCheckboxCaptcha = () => {
      if (
        cancelled ||
        widgetIdRef.current !== null ||
        !containerRef.current ||
        typeof window.grecaptcha?.render !== "function"
      ) {
        return false;
      }

      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        callback: onTokenChange,
        "expired-callback": () => onTokenChange(""),
        "error-callback": () => onTokenChange(""),
      });
      return true;
    };

    const executeV3Captcha = () => {
      if (cancelled || typeof window.grecaptcha?.ready !== "function") return false;

      window.grecaptcha.ready(() => {
        if (cancelled || typeof window.grecaptcha?.execute !== "function") return;
        window.grecaptcha
          .execute(siteKey, { action: "signup" })
          .then((token) => {
            if (!cancelled) onTokenChange(token || "");
          })
          .catch(() => {
            if (!cancelled) onTokenChange("");
          });
      });
      return true;
    };

    const startCaptcha = () => {
      if (normalizedMode === "v3") {
        if (executeV3Captcha()) {
          if (timer) window.clearInterval(timer);
        }
        return;
      }

      if (renderCheckboxCaptcha() && timer) {
        window.clearInterval(timer);
      }
    };

    const attachScript = () => {
      const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
      if (existingScript) {
        if (existingScript.getAttribute("src") !== scriptSrc) {
          existingScript.remove();
          delete window.grecaptcha;
        } else {
          return existingScript;
        }
      }

      const script = document.createElement("script");
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.onload = startCaptcha;
      document.body.appendChild(script);
      return script;
    };

    attachScript();
    timer = window.setInterval(startCaptcha, 100);
    startCaptcha();

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [mode, onTokenChange, siteKey]);

  if (!siteKey) {
    return <Alert severity="warning">{t("signup.captchaMissing")}</Alert>;
  }

  if (mode === "v3") {
    return (
      <Typography variant="caption" color="text.secondary">
        {t("signup.captchaProtected")}
      </Typography>
    );
  }

  return <Box ref={containerRef} sx={{ minHeight: 78 }} />;
}

const uiLangToDisplay = (lng) => {
  const code = (lng || "fr").slice(0, 2);
  return ["fr", "en", "he"].includes(code) ? code : "fr";
};

export default function SignUpPage() {
  const { t, i18n } = useTranslation();
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const recaptchaMode = process.env.REACT_APP_RECAPTCHA_MODE === "v3" ? "v3" : "checkbox";
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    communityName: "",
    communitySlug: "",
    address: "",
    displayLanguage: uiLangToDisplay(i18n.language),
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [addressStatus, setAddressStatus] = useState("idle");
  const [addressError, setAddressError] = useState("");
  const [validatedAddressLine, setValidatedAddressLine] = useState("");
  const lastValidatedAddressRef = useRef("");
  const addressValidateRequestIdRef = useRef(0);

  useEffect(() => {
    if (slugTouched) return;
    setForm((prev) => ({ ...prev, communitySlug: slugify(prev.communityName) }));
  }, [form.communityName, slugTouched]);

  const updateField = useCallback((field) => {
    return (event) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
      setError("");
      if (field === "address") {
        const next = event.target.value;
        if (next.trim() !== lastValidatedAddressRef.current) {
          setAddressStatus("idle");
          setAddressError("");
          setValidatedAddressLine("");
        }
      }
    };
  }, []);

  const validateAddressField = useCallback(async () => {
    const trimmed = form.address.trim();
    if (trimmed.length < 5) {
      setAddressStatus("invalid");
      setAddressError(t("signup.errors.addressTooShort"));
      setValidatedAddressLine("");
      lastValidatedAddressRef.current = "";
      return false;
    }

    const requestId = ++addressValidateRequestIdRef.current;
    setAddressStatus("validating");
    setAddressError("");
    setValidatedAddressLine("");

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/validate-address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: trimmed }),
      });
      const json = await response.json().catch(() => null);
      if (requestId !== addressValidateRequestIdRef.current) return false;

      if (!response.ok) {
        setAddressStatus("invalid");
        setAddressError(json?.message || t("signup.errors.geocode"));
        setValidatedAddressLine("");
        lastValidatedAddressRef.current = "";
        return false;
      }

      lastValidatedAddressRef.current = trimmed;
      setAddressStatus("valid");
      setValidatedAddressLine(json?.address || trimmed);
      setAddressError("");
      return true;
    } catch {
      if (requestId !== addressValidateRequestIdRef.current) return false;
      setAddressStatus("invalid");
      setAddressError(t("signup.errors.geocodeUnavailable"));
      setValidatedAddressLine("");
      lastValidatedAddressRef.current = "";
      return false;
    }
  }, [form.address, t]);

  const handleAddressBlur = useCallback(() => {
    if (!form.address.trim()) {
      setAddressStatus("idle");
      setAddressError("");
      setValidatedAddressLine("");
      return;
    }
    validateAddressField();
  }, [form.address, validateAddressField]);

  const updateSlug = useCallback((event) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, communitySlug: slugify(event.target.value) }));
    setError("");
  }, []);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      setError("");
      setSuccess(false);

      if (form.password !== form.confirmPassword) {
        setError(t("signup.errors.passwordMismatch"));
        return;
      }
      if (form.password.length < 8) {
        setError(t("signup.errors.passwordLength"));
        return;
      }

      const addressOk =
        addressStatus === "valid" && form.address.trim() === lastValidatedAddressRef.current
          ? true
          : await validateAddressField();
      if (!addressOk) {
        setError(t("signup.errors.addressNotValidated"));
        return;
      }

      try {
        setLoading(true);
        let submitCaptchaToken = captchaToken;
        if (siteKey && recaptchaMode === "v3") {
          submitCaptchaToken = await executeRecaptchaV3(siteKey);
          setCaptchaToken(submitCaptchaToken || "");
        }
        if (siteKey && !submitCaptchaToken) {
          setError(t("signup.errors.captcha"));
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/register-free`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fullName: form.fullName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            password: form.password,
            communityName: form.communityName.trim(),
            communitySlug: form.communitySlug.trim(),
            address: form.address.trim(),
            language: i18n.language || "fr",
            displayLanguage: form.displayLanguage,
            captchaToken: submitCaptchaToken,
          }),
        });
        const json = await response.json().catch(() => null);
        if (!response.ok) {
          const code = json?.code;
          if (code === "GEOCODE_NOT_FOUND" || code === "GEOCODE_INVALID" || code === "GEOCODE_FAILED") {
            throw new Error(json?.message || t("signup.errors.geocode"));
          }
          throw new Error(json?.message || t("signup.errors.submit"));
        }
        setSuccess(true);
        setForm({
          fullName: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: "",
          communityName: "",
          communitySlug: "",
          address: "",
          displayLanguage: "fr",
        });
        setAddressStatus("idle");
        setAddressError("");
        setValidatedAddressLine("");
        lastValidatedAddressRef.current = "";
        setCaptchaToken("");
        if (recaptchaMode !== "v3" && typeof window.grecaptcha?.reset === "function") {
          window.grecaptcha.reset();
        }
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    },
    [addressStatus, captchaToken, form, i18n.language, recaptchaMode, siteKey, t, validateAddressField]
  );

  const addressHelperText =
    addressStatus === "invalid" ? (
      addressError
    ) : addressStatus === "valid" && validatedAddressLine ? (
      <Box component="span" sx={{ display: "block" }}>
        <Box component="span" sx={{ color: "success.main" }}>
          {t("signup.addressValid")}
        </Box>
        <Box component="span" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
          {validatedAddressLine}
        </Box>
      </Box>
    ) : (
      t("signup.addressHelp")
    );

  return (
    <Container maxWidth="sm" sx={{ pt: 14, pb: 8 }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 700 }}>
            {t("signup.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("signup.subtitle")}
          </Typography>

          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("signup.success")}
            </Alert>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField label={t("signup.fields.fullName")} value={form.fullName} onChange={updateField("fullName")} required />
            <TextField label={t("signup.fields.email")} type="email" value={form.email} onChange={updateField("email")} required />
            <TextField label={t("signup.fields.phone")} value={form.phone} onChange={updateField("phone")} required />
            <TextField
              label={t("signup.fields.password")}
              type="password"
              value={form.password}
              onChange={updateField("password")}
              required
            />
            <TextField
              label={t("signup.fields.confirmPassword")}
              type="password"
              value={form.confirmPassword}
              onChange={updateField("confirmPassword")}
              required
            />
            <TextField
              label={t("signup.fields.communityName")}
              value={form.communityName}
              onChange={updateField("communityName")}
              required
            />
            <TextField
              label={t("signup.fields.address")}
              value={form.address}
              onChange={updateField("address")}
              onBlur={handleAddressBlur}
              required
              multiline
              minRows={2}
              error={addressStatus === "invalid"}
              helperText={addressHelperText}
              FormHelperTextProps={
                addressStatus === "valid"
                  ? { sx: { color: "success.main" } }
                  : undefined
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: "flex-start"}}>
                    {addressStatus === "validating" ? (
                      <CircularProgress size={22} aria-label={t("signup.addressValidating")} />
                    ) : null}
                    {addressStatus === "valid" ? (
                      <CheckCircleOutlineIcon color="success" aria-label={t("signup.addressValid")} />
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth required>
              <InputLabel id="signup-display-language-label">
                {t("signup.fields.displayLanguage")}
              </InputLabel>
              <Select
                labelId="signup-display-language-label"
                label={t("signup.fields.displayLanguage")}
                value={form.displayLanguage}
                onChange={updateField("displayLanguage")}
              >
                <MenuItem value="fr">{t("language.french")}</MenuItem>
                <MenuItem value="en">{t("language.english")}</MenuItem>
                <MenuItem value="he">{t("language.hebrew")}</MenuItem>
              </Select>
              <FormHelperText>{t("signup.displayLanguageHelp")}</FormHelperText>
            </FormControl>
            <TextField
              label={t("signup.fields.communitySlug")}
              value={form.communitySlug}
              onChange={updateSlug}
              helperText={t("signup.slugHelp", { slug: form.communitySlug || "your-community" })}
              required
            />

            <RecaptchaBox siteKey={siteKey} mode={recaptchaMode} onTokenChange={setCaptchaToken} />

            <Button type="submit" variant="contained" size="large" disabled={loading}>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  {t("signup.creating")}
                </Box>
              ) : (
                t("signup.submit")
              )}
            </Button>
            <Button component={RouterLink} to="/SignInPage" variant="text">
              {t("signup.haveAccount")}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
